import { LocalStorageKeys } from "@/shared/enums/local-storage-keys.enum";

export const CUSTOM_LAYER_EDIT_SESSION_TTL_MS = 60 * 60 * 1000;

export interface CustomLayerEditSessionEntry {
  layerId: string;
  startedAt: number;
}

export type CustomLayerEditSessionStored = Record<string, CustomLayerEditSessionEntry>;

function readStored(): CustomLayerEditSessionStored {
  if (typeof window === "undefined") return {};

  try {
    const raw = window.localStorage.getItem(LocalStorageKeys.CustomLayerEditSession);
    if (!raw) return {};

    const parsed = JSON.parse(raw) as unknown;
    if (parsed == null || typeof parsed !== "object" || Array.isArray(parsed)) {
      return {};
    }

    return parsed as CustomLayerEditSessionStored;
  } catch {
    return {};
  }
}

function writeStored(stored: CustomLayerEditSessionStored): void {
  if (typeof window === "undefined") return;

  if (Object.keys(stored).length === 0) {
    window.localStorage.removeItem(LocalStorageKeys.CustomLayerEditSession);
    return;
  }

  window.localStorage.setItem(LocalStorageKeys.CustomLayerEditSession, JSON.stringify(stored));
}

function isSessionEntryValid(entry: CustomLayerEditSessionEntry | undefined): entry is CustomLayerEditSessionEntry {
  if (!entry || typeof entry.layerId !== "string" || entry.layerId === "") {
    return false;
  }

  if (typeof entry.startedAt !== "number" || Number.isNaN(entry.startedAt)) {
    return false;
  }

  return Date.now() - entry.startedAt <= CUSTOM_LAYER_EDIT_SESSION_TTL_MS;
}

export function setCustomLayerEditSession(districtId: string, layerId: string): void {
  const stored = readStored();
  stored[districtId] = { layerId, startedAt: Date.now() };
  writeStored(stored);
}

export function clearCustomLayerEditSession(districtId: string | null | undefined): void {
  if (!districtId) return;

  const stored = readStored();
  if (!(districtId in stored)) return;

  delete stored[districtId];
  writeStored(stored);
}

export function getCustomLayerEditSessionLayerId(districtId: string | null | undefined): string | null {
  if (!districtId) return null;

  const entry = readStored()[districtId];
  if (!isSessionEntryValid(entry)) {
    clearCustomLayerEditSession(districtId);
    return null;
  }

  return entry.layerId;
}

export function getProtectedCustomLayerIdsForPrune(
  districtId: string | null | undefined,
): ReadonlySet<string> | undefined {
  const layerId = getCustomLayerEditSessionLayerId(districtId);
  return layerId ? new Set([layerId]) : undefined;
}
